// By person. What each name is carrying, and whether they can see it.
//
// The has_login column is not decoration. Two names on the imported board
// cannot sign in: Kgotlelelo Petje, who carries 32 tasks, and "Deleted member",
// who carries 18 and is not a person at all. A board that shows a person's work
// to everyone except that person is a management report, not a task board, and
// this view says so on the row rather than in a footnote.
import { AlertTriangle, UserX } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import { bubbleClass, initials, type TaskPerson } from '../../utils/tasks'

export function TaskPeople({
  people,
  selected,
  onSelect,
}: {
  people: TaskPerson[]
  selected: string[]
  onSelect: (name: string) => void
}) {
  if (people.length === 0) {
    return <p className="p-6 text-sm text-muted-foreground">Nobody is carrying a task yet.</p>
  }

  return (
    <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
      {people.map((p) => {
        const active = selected.includes(p.name)
        return (
          <button
            key={p.name}
            type="button"
            onClick={() => onSelect(p.name)}
            className={cn(
              'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
              active
                ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]'
                : 'border-border bg-card hover:border-foreground/20',
            )}
          >
            <span
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white',
                bubbleClass(p.name),
              )}
            >
              {initials(p.name)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-sm font-medium">{p.name}</span>
                {!p.has_login && (
                  <span
                    className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200"
                    title="This person has no cockpit_access row, so they cannot open this board and cannot see their own tasks."
                  >
                    <UserX className="h-3 w-3" />
                    No login
                  </span>
                )}
                {p.person_id === null && (
                  <span
                    className="inline-flex items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-900 dark:bg-rose-950 dark:text-rose-200"
                    title="This name does not match anybody on record, so nothing can be emailed to them and no action can be attributed."
                  >
                    <AlertTriangle className="h-3 w-3" />
                    Not a person on record
                  </span>
                )}
              </div>
              <p className="truncate text-xs text-muted-foreground">{p.email ?? 'No email on file'}</p>
              <div className="mt-2 flex items-center gap-3 text-xs">
                <span className="tabular-nums">
                  <span className="font-semibold">{p.open_tasks}</span>{' '}
                  <span className="text-muted-foreground">open</span>
                </span>
                <span
                  className={cn(
                    'tabular-nums',
                    p.overdue_tasks > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground',
                  )}
                >
                  <span className="font-semibold">{p.overdue_tasks}</span> overdue
                </span>
                <span className="ml-auto tabular-nums text-muted-foreground">
                  {p.total_tasks} all time
                </span>
              </div>
            </div>
          </button>
        )
      })}
    </div>
  )
}
